// FLAGS: silent-failure
// Runs once before React mounts: resolves the LanguagePathPrefix flag (fail-closed, bounded wait), takes the router
// basename from the address, initialises i18next with the flag and the profile language, applies `<html lang>` and
// the date locale, reports a resolution miss, and with the flag on prefixes a bare address. Last, whatever happened,
// it loads the Cookiebot banner in the resolved language. A failure while resolving boots the app in legacy mode; a
// failure registering the interceptors makes `boot()` reject and `main.tsx` show the failure cover.
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { appInsights } from "@src/appInsights/AppInsights";
import { registerInterceptors } from "@src/AxiosSetup";
import {
  BOOT_TELEMETRY_PROPERTIES,
  RESOLVE_TELEMETRY_PROPERTIES,
  reportBootFailure,
} from "@src/boot/bootLoader";
import {
  applyLanguageSideEffects,
  followLanguageChanges,
} from "@src/boot/languageSideEffects";
import getStoredProfileLanguage from "@src/boot/storedProfileLanguage";
import { setRouterBasename } from "@src/boot/routerBasename";
import injectCookiebot from "@src/boot/cookiebot";
import i18n, {
  LanguageCandidate,
  LanguageInputs,
  LanguageResolution,
  collectLanguageCandidates,
  describeLanguageResolution,
  initI18n,
  translatePathToLocale,
} from "@src/i18n";
import {
  forgetAddedLanguagePrefix,
  getAddedLanguagePrefix,
  getPickedLanguage,
  rememberAddedLanguagePrefix,
} from "@src/utils/addedLanguagePrefix";
import {
  firstPathSegment,
  getLanguageForPath,
  getLanguagePathPrefix,
  hasLanguagePathPrefix,
  prefixForLanguage,
  routerBasenameFor,
  stripLanguagePathPrefix,
} from "@src/utils/languagePathPrefix";
import { getFeatureFlagValue } from "@src/services/featureFlagService";

export const BOOT_FLAG_TIMEOUT_MS = 1500;

export interface BootFlags {
  languagePathPrefix: boolean;
}

const bootFlags: BootFlags = { languagePathPrefix: false };

// FLAGS: silent-failure
// A failing flag endpoint reads as off; a slow one reads as off and leaves one warning trace.
const resolveFlag = async (key: string): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      appInsights.trackTrace({
        message: `Boot flag ${key} timed out after ${BOOT_FLAG_TIMEOUT_MS} ms; booting in legacy mode`,
        severityLevel: SeverityLevel.Warning,
        properties: { ...BOOT_TELEMETRY_PROPERTIES, flag: key },
      });
      resolve(false);
    }, BOOT_FLAG_TIMEOUT_MS);
  });
  try {
    return await Promise.race([getFeatureFlagValue(key), timeout]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

// The flag decision every later reader must use — never re-read it with `useFeatureFlag` for boot-level choices.
export const getBootFlags = (): Readonly<BootFlags> =>
  Object.freeze({ ...bootFlags });

// FLAGS: silent-failure
// i18next falls back silently, so this trace is the only sign a bad prefix, profile code or host rendered the
// fallback language. One Warning per page load, only on a miss; emitted flag-off too, to baseline hostname misses.
const reportLanguageMiss = (
  languagePathPrefix: boolean,
  resolution: LanguageResolution,
): void => {
  if (!resolution.miss) {
    return;
  }
  appInsights.trackTrace({
    message: "Language resolution miss",
    severityLevel: SeverityLevel.Warning,
    properties: {
      ...RESOLVE_TELEMETRY_PROPERTIES,
      reason: resolution.miss,
      source: resolution.resolved?.source ?? "fallback",
      resolved: resolution.resolved?.code ?? "",
      applied: resolution.applied,
      segment: firstPathSegment(window.location.pathname),
      hostname: window.location.hostname,
      languagePathPrefix,
    },
  });
};

// Called back on a registered bare address, so never prefixed: the Phase Two callback redeems its code with its own
// address as the redirect URI, and the PwC opener polls its popup there.
const BARE_ONLY_PATH = /^\/auth\/(callback|pwc)(\/|$)/i;

// Strips a prefix before detection and answers its language, so the path can be translated from it. Flag on: a
// prefix the app guessed earlier in this tab, or one that differs from a signed-in user's saved language (which they
// always see); any other prefix is the visitor's choice and stays. Flag off: any prefix, as no basename is mounted.
const takeBackPrefix = (
  pathname: string,
  languagePathPrefix: boolean,
  profileLanguage: string | undefined,
): string | undefined => {
  const prefix = getLanguagePathPrefix(pathname);
  if (prefix === undefined) {
    return undefined;
  }
  const profilePrefix =
    profileLanguage === undefined
      ? undefined
      : prefixForLanguage(profileLanguage);
  const replaceable =
    prefix === getAddedLanguagePrefix() ||
    (profilePrefix !== undefined && profilePrefix !== prefix);
  if (!languagePathPrefix || !replaceable) {
    forgetAddedLanguagePrefix();
  }
  if (languagePathPrefix && !replaceable) {
    return undefined;
  }
  const { search, hash } = window.location;
  window.history.replaceState(
    null,
    "",
    `${stripLanguagePathPrefix(pathname)}${search}${hash}`,
  );
  return getLanguageForPath(pathname);
};

// Route segments are translation keys: translate the bare path into the applied language before React mounts, or
// it misses the rebuilt route table. It is written in the stripped prefix's language or, when the profile
// answered, the hostname's. Answers the resulting path.
const alignPathWithLanguage = (
  resolution: LanguageResolution,
  pathname: string,
  takenBackLanguage: string | undefined,
): string => {
  // The hostname's pre-init answer. Re-reading it now would return the language init just cached, and the
  // rewrite would never happen.
  const pathLanguage =
    takenBackLanguage ??
    (resolution.resolved?.source === "profile"
      ? resolution.candidates.find(
          (candidate) => candidate.source === "hostname",
        )?.code
      : undefined);
  if (!pathLanguage || pathLanguage === i18n.language) {
    return pathname;
  }
  const translated = translatePathToLocale(
    pathname,
    pathLanguage,
    i18n.language,
  );
  if (translated === pathname) {
    return pathname;
  }
  const { search, hash } = window.location;
  window.history.replaceState(null, "", `${translated}${search}${hash}`);
  return translated;
};

// A bare address gets the applied language's prefix and the router mounts under it; marked as the app's guess.
const addLanguagePrefix = (pathname: string): void => {
  if (hasLanguagePathPrefix(pathname) || BARE_ONLY_PATH.test(pathname)) {
    return;
  }
  const prefix = prefixForLanguage(i18n.language);
  if (prefix === undefined) {
    return;
  }
  const { search, hash } = window.location;
  window.history.replaceState(
    null,
    "",
    `/${prefix}${pathname}${search}${hash}`,
  );
  setRouterBasename(`/${prefix}`, true);
  rememberAddedLanguagePrefix(prefix);
};

const resolveLanguage = async (): Promise<void> => {
  try {
    bootFlags.languagePathPrefix = await resolveFlag("LanguagePathPrefix");
    let { pathname } = window.location;
    // Read before init (which caches its answer) and before the prefix is taken back, which it decides for a
    // signed-in user. Flag on only; own try so blocked storage is reported on its own.
    let profileLanguage: string | undefined;
    try {
      if (bootFlags.languagePathPrefix) {
        profileLanguage = getStoredProfileLanguage();
      }
    } catch (error) {
      reportBootFailure(error);
    }
    const takenBackLanguage = takeBackPrefix(
      pathname,
      bootFlags.languagePathPrefix,
      profileLanguage,
    );
    if (takenBackLanguage !== undefined) {
      pathname = stripLanguagePathPrefix(pathname);
    }
    // A bare address gets its prefix once the language is known, below.
    setRouterBasename(
      routerBasenameFor(pathname, bootFlags.languagePathPrefix),
      bootFlags.languagePathPrefix,
    );
    // Signed out, the tab's picked language stands in for detection only: the prefix decision above takes the saved
    // language alone, so a pick never replaces a prefix the visitor chose.
    const detectedProfileLanguage = bootFlags.languagePathPrefix
      ? (profileLanguage ?? getPickedLanguage())
      : undefined;
    const inputs: LanguageInputs = {
      languagePathPrefix: bootFlags.languagePathPrefix,
      ...(detectedProfileLanguage !== undefined && {
        profileLanguage: detectedProfileLanguage,
      }),
    };
    let candidates: LanguageCandidate[] = [];
    try {
      candidates = collectLanguageCandidates(inputs);
    } catch (error) {
      reportBootFailure(error);
    }
    initI18n(inputs);
    applyLanguageSideEffects(i18n.language);
    followLanguageChanges(i18n);
    const resolution = describeLanguageResolution(candidates);
    // Report first, so a throw in the rewrite cannot lose the trace.
    reportLanguageMiss(bootFlags.languagePathPrefix, resolution);
    pathname = alignPathWithLanguage(resolution, pathname, takenBackLanguage);
    if (bootFlags.languagePathPrefix) {
      addLanguagePrefix(pathname);
    }
  } catch (error) {
    reportBootFailure(error);
  }
};

const boot = async (): Promise<Readonly<BootFlags>> => {
  try {
    registerInterceptors();
    await resolveLanguage();
  } finally {
    // English when i18n never initialised.
    injectCookiebot(i18n.language);
  }
  return getBootFlags();
};

export default boot;
